/**
 * Cabecera del día (GEN-PX-008, spec §4.1). Sección 1 del orden fijo. Siempre existe.
 * Izquierda: día y hora + saludo con el nombre + (GEN-PX-009) titular, entradilla y firma del
 * agente enrutador. Derecha: cifras de lo que existe.
 *
 * El saludo sale al momento; el resumen llega después y nunca bloquea (indicador discreto mientras
 * tanto; si falla, el saludo se queda sin texto). Los nombres en negrita de la entradilla abren su
 * conversación: el kernel ya validó los ids, aquí solo se pintan.
 */

import { useEffect, useState } from 'react'
import Cifras from './Cifras.jsx'
import { Avatar } from '../ui.jsx'
import { useFotoAgente } from '../../context/FotosAgentes.jsx'
import useResumenDia, { tituloCabecera } from './useResumenDia.js'
import { cifrasDe, fechaHora, msHastaMinuto, primerNombre, saludoDe } from './cabeceraDia.js'

/** Hora local que se refresca al cambiar el minuto mientras la página siga abierta. */
function useAhora() {
  const [ahora, setAhora] = useState(() => new Date())
  useEffect(() => {
    let t
    const programa = () => {
      t = setTimeout(() => { setAhora(new Date()); programa() }, msHastaMinuto(new Date()) + 50)
    }
    programa()
    return () => clearTimeout(t)
  }, [])
  return ahora
}

const iniciales = (n) => (n ?? '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()

/** Firma con el nombre y el cargo reales del agente que escribió el resumen (los da el kernel). */
function Firma({ agente, agents }) {
  const fotoDe = useFotoAgente()
  const ficha = agents.find(a => a.id === agente.id)
  return (
    <div className="dk-cab__firma">
      <Avatar foto={ficha ? fotoDe(ficha) : null} iniciales={iniciales(agente.nombre)} nombre={agente.nombre} tamano={28} agente />
      <span>Resumen preparado por {agente.nombre}{agente.rol ? `, ${agente.rol}` : ''}</span>
    </div>
  )
}

function Entradilla({ segmentos, onSelectChat }) {
  return (
    <p className="dk-cab__p">
      {segmentos.map((s, i) => s.chat_id && onSelectChat
        ? <b key={i}><button type="button" className="dk-cab__enlace" onClick={() => onSelectChat(s.chat_id)}>{s.texto}</button></b>
        : s.chat_id ? <b key={i}>{s.texto}</b> : <span key={i}>{s.texto}</span>)}
    </p>
  )
}

export default function Cabecera({ user, disponibles = [], chats = [], agents = [], enrutadora = 'Anne', onSelectChat }) {
  const ahora = useAhora()
  const nombre = primerNombre(user?.display_name)
  const { estado, datos } = useResumenDia()
  const conTexto = estado === 'listo' && datos?.titular
  const saludo = `${saludoDe(ahora)}${nombre ? `, ${nombre}` : ''}`
  return (
    <header className="dk-cab" aria-label="Cabecera del día">
      <div className="dk-cab__texto">
        <div className="dk-cab__eyebrow">{fechaHora(ahora)}</div>
        <h1 className="dk-cab__h1">{tituloCabecera(saludo, conTexto ? datos.titular : '')}</h1>
        {estado === 'cargando' && (
          <div className="dk-cab__preparando" role="status">
            <i aria-hidden="true" />{enrutadora} está preparando tu resumen…
          </div>
        )}
        {conTexto && datos.entradilla?.length > 0 && (
          <>
            <Entradilla segmentos={datos.entradilla} onSelectChat={onSelectChat} />
            {datos.agente && <Firma agente={datos.agente} agents={agents} />}
          </>
        )}
      </div>
      <Cifras items={cifrasDe({ disponibles, chats }, ahora)} />
    </header>
  )
}
